import { useEffect, useState } from 'react';
import {
  applyDaylight,
  clearDaylight,
  computeDaylight,
  type DaylightState,
  type DaylightStrength,
} from './daylight';

export interface UseDaylightOptions {
  latitude: number;
  longitude: number;
  strength?: DaylightStrength | number;
  nightTheme?: 'dark' | 'wall-night';
  /** Element that receives the colours; defaults to <html>. */
  target?: HTMLElement | null;
  /** Fixed moment (Storybook time slider). Without it the real clock is used, updated each minute. */
  at?: Date;
  enabled?: boolean;
}

/**
 * Lets the ambient colours follow the sun. One recalculation per minute; the steps are far
 * below what the eye notices, so the flow looks continuous.
 */
export function useDaylight({
  latitude,
  longitude,
  strength = 'subtle',
  nightTheme = 'wall-night',
  target,
  at,
  enabled = true,
}: UseDaylightOptions): DaylightState | undefined {
  const [state, setState] = useState<DaylightState>();
  const atMs = at?.getTime();

  useEffect(() => {
    if (!enabled) return;
    const el = target ?? document.documentElement;
    const previousTheme = el.getAttribute('data-theme');
    const tick = () => {
      const next = computeDaylight(
        atMs === undefined ? new Date() : new Date(atMs),
        latitude,
        longitude,
        strength,
      );
      applyDaylight(el, next, { nightTheme });
      setState(next);
    };
    tick();
    const timer = atMs === undefined ? window.setInterval(tick, 60_000) : undefined;
    return () => {
      window.clearInterval(timer);
      clearDaylight(el, previousTheme);
    };
  }, [enabled, target, atMs, latitude, longitude, strength, nightTheme]);

  return enabled ? state : undefined;
}
